import React, { useEffect, useState } from 'react'

interface FullscreenComponentProps {
    ContainerRef: React.RefObject<HTMLDivElement | null>;
    isFullscreen?: boolean;
    onFullscreenChange?: (isFullscreen: boolean) => void;
}

/**
 * Toggle fullscreen mode for a container element.
 * If isFullscreen and onFullscreenChange are provided, they will be used to track the fullscreen state.
 * Otherwise, the function will just toggle the fullscreen state.
 */
export const toggleFullscreen = ({
    ContainerRef, 
    isFullscreen, 
    onFullscreenChange
}: FullscreenComponentProps) => {
    if (ContainerRef.current) {
        if (!isFullscreen && !document.fullscreenElement) {
            ContainerRef.current?.requestFullscreen().catch((err) => {
                console.error(`Error attempting to enable full-screen mode: ${err.message}`);
            });
            if (onFullscreenChange) onFullscreenChange(true);
        } else {
            if (document.fullscreenElement) {
                document.exitFullscreen().catch((err) => {
                    console.error(`Error attempting to exit full-screen mode: ${err.message}`);
                });
                if (onFullscreenChange) onFullscreenChange(false);
            }
        }
    }
}

/**
 * hook cho phép chuyển đổi giữa chế độ fullscreen và chế độ bình thường
 * Returns [isFullscreen, toggleFullscreen]
 */
export const useFullscreen = (containerRef: React.RefObject<HTMLDivElement | null>) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    
    // Add listeners to detect fullscreen changes from the browser
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);
    
    const handleToggleFullscreen = () => {
        toggleFullscreen({ 
            ContainerRef: containerRef, 
            isFullscreen,
            onFullscreenChange: setIsFullscreen
        });
    };
    
    return [isFullscreen, handleToggleFullscreen] as const;
}